import { useCallback, useEffect, useRef, useState } from 'react'

type Updater<T> = T | ((previous: T) => T)

/**
 * SSR-safe localStorage state. The first render always uses `defaultValue` on both
 * server and client; the stored value is read once after mount and `ready` flips to true.
 */
export function usePersistentState<T>(key: string, defaultValue: T): [T, (next: Updater<T>) => void, boolean] {
    const [value, setValue] = useState<T>(defaultValue)
    const [ready, setReady] = useState(false)
    const valueRef = useRef<T>(defaultValue)

    useEffect(() => {
        try {
            const raw = window.localStorage.getItem(key)

            if (raw !== null) {
                const parsed = JSON.parse(raw) as T

                valueRef.current = parsed
                setValue(parsed)
            }
        }
        catch {
            // Unreadable or blocked storage: keep the default value.
        }

        setReady(true)
    }, [key])

    const update = useCallback((next: Updater<T>) => {
        const resolved = typeof next === 'function'
            ? (next as (previous: T) => T)(valueRef.current)
            : next

        valueRef.current = resolved
        setValue(resolved)

        try {
            window.localStorage.setItem(key, JSON.stringify(resolved))
        }
        catch {
            // Storage full or blocked: the in-memory value still updates.
        }
    }, [key])

    return [value, update, ready]
}
